// The 36 pairings of the Body at Work (Taylor's v4.1 doc, Oct 22, 2026: "Draft names and copy
// for Taylor to rewrite in his voice"), transcribed word for word from
// docs/source/taylor-question-bank-v4.1-2026-10-22.txt. Each pairing's type, blind spot and
// office echo are worked out in ./pairing.ts, and its tests check them against the doc's table.

export interface PairingCopy {
  /** The two gifts, in the doc's order. */
  gifts: [string, string];
  name: string;
  oneLine: string;
  craves: string;
  crushedBy: string;
  /** How it shows when freedom is low (Caged and Fragmented only). */
  bound: string;
}

export const PAIRING_COPY: PairingCopy[] = [
  {
    gifts: ["Prophecy", "Pioneering"],
    name: "The Seer-Builder",
    oneLine: "Sees what's missing and starts the thing that fills it.",
    craves: "Freedom to act on what they see; people who trust their read",
    crushedBy: "Committees that slow a clear call; being told to prove it",
    bound: "Starts things to prove they were right, then abandons them when challenged",
  },
  {
    gifts: ["Prophecy", "Teaching"],
    name: "The Truth Teacher",
    oneLine: "Names what's true and makes it understandable.",
    craves: "Honest questions; room to go deep",
    crushedBy: "Shallow agreement; being told to soften it into nothing",
    bound: "Right and harsh; uses truth to win the argument",
  },
  {
    gifts: ["Prophecy", "Outreach"],
    name: "The Bold Witness",
    oneLine: "Says what's true to people who haven't heard it yet.",
    craves: "Real conversations with outsiders; courage in the room",
    crushedBy: "Insider politics; being muted to keep people comfortable",
    bound: "Provokes for a reaction and needs the crowd to agree",
  },
  {
    gifts: ["Prophecy", "Exhorting"],
    name: "The Truth-Telling Coach",
    oneLine: "Names what's wrong and calls you higher.",
    craves: "Honest rooms; people who want to grow",
    crushedBy: "Flattery and politics",
    bound: "Hard words, then pressure",
  },
  {
    gifts: ["Prophecy", "Leading"],
    name: "The Course Corrector",
    oneLine: "Sees what's off and reorganizes to fix it.",
    craves: "Authority to adjust; clear priorities",
    crushedBy: "Ignored warnings; chaos nobody will name",
    bound: "Controls everything because they saw it coming",
  },
  {
    gifts: ["Prophecy", "Giving"],
    name: "The Discerning Giver",
    oneLine: "Sees where resources will matter most and releases them there.",
    craves: "Real needs; trustworthy information",
    crushedBy: "Guilt appeals and manipulation; waste",
    bound: "Gives with strings and judgment attached",
  },
  {
    gifts: ["Prophecy", "Serving"],
    name: "The Quiet Seer",
    oneLine: "Sees what's needed and quietly handles it.",
    craves: "Freedom to just take care of it; being trusted",
    crushedBy: "Being ignored until it's a crisis; busywork",
    bound: "The resentful fixer who saw it first and makes sure you know",
  },
  {
    gifts: ["Prophecy", "Mercy"],
    name: "The Tender Truth-Teller",
    oneLine: "Tells the truth with a hand on your shoulder.",
    craves: "Safe places to be honest; time with people",
    crushedBy: "Being forced to be harsh or forced to stay silent",
    bound: "Swallows the truth so nobody gets hurt",
  },
  {
    gifts: ["Pioneering", "Teaching"],
    name: "The Architect",
    oneLine: "Builds new things and makes them learnable.",
    craves: "Room to build; people who'll learn the model",
    crushedBy: "Maintaining what's already built; detail work",
    bound: "Starts frameworks to prove brilliance; needs to be right about the new thing",
  },
  {
    gifts: ["Pioneering", "Outreach"],
    name: "The Movement Starter",
    oneLine: "Starts new things and brings people into them.",
    craves: "A blank page and people to invite; momentum",
    crushedBy: "Slow approvals; empty rooms",
    bound: "Launches for applause and measures worth by turnout",
  },
  {
    gifts: ["Pioneering", "Exhorting"],
    name: "The Launcher",
    oneLine: "Starts things and gets people believing they can do it too.",
    craves: "Bold goals; people ready to step up",
    crushedBy: "Cynicism; endless planning",
    bound: "Hype-driven starts that outrun the substance",
  },
  {
    gifts: ["Pioneering", "Leading"],
    name: "The Founder",
    oneLine: "Starts it and builds the structure to make it last.",
    craves: "Authority to build; a team to organize",
    crushedBy: "Inheriting a mess with no say; red tape",
    bound: "Holds on too tight and can't hand it off",
  },
  {
    gifts: ["Pioneering", "Giving"],
    name: "The Backer",
    oneLine: "Starts and funds new things.",
    craves: "Worthy ideas; freedom to invest",
    crushedBy: "Wasted resources; timid plans",
    bound: "Funds things to control them or to buy significance",
  },
  {
    gifts: ["Pioneering", "Serving"],
    name: "The Hands-On Builder",
    oneLine: "Builds the first version with their own hands.",
    craves: "Practical problems; tools and freedom",
    crushedBy: "Meetings about doing; theory with no build",
    bound: "Does it all alone and can't let others in",
  },
  {
    gifts: ["Pioneering", "Mercy"],
    name: "The Rescue Builder",
    oneLine: "Starts things for people who are hurting.",
    craves: "A cause with faces; freedom to respond",
    crushedBy: "Indifference; red tape that ignores pain",
    bound: "Starts rescues they can't sustain and burns out carrying everyone",
  },
  {
    gifts: ["Teaching", "Outreach"],
    name: "The Explainer",
    oneLine: "Makes truth clear to people who've never heard it.",
    craves: "Curious newcomers; open questions",
    crushedBy: "Insiders who've stopped listening; watered-down answers",
    bound: "Argues to win converts",
  },
  {
    gifts: ["Teaching", "Exhorting"],
    name: "The Coach",
    oneLine: "Teaches people, then pushes them to live it.",
    craves: "People who do the work; growth they can see",
    crushedBy: "Passive listeners; learning without change",
    bound: "Lectures and pushes to feel useful",
  },
  {
    gifts: ["Teaching", "Leading"],
    name: "The Systems Builder",
    oneLine: "Turns understanding into a system people can run.",
    craves: "Clarity; a team that follows the playbook",
    crushedBy: "Improvisers who skip the steps; chaos",
    bound: "Rigid: the system matters more than the people",
  },
  {
    gifts: ["Teaching", "Giving"],
    name: "The Investor in People",
    oneLine: "Teaches and resources others to grow.",
    craves: "Hungry learners; ways to multiply",
    crushedBy: "Entitlement; waste",
    bound: "Gives knowledge and money to be needed",
  },
  {
    gifts: ["Teaching", "Serving"],
    name: "The Practical Teacher",
    oneLine: "Shows you how by doing it alongside you.",
    craves: "Hands-on learners; real tasks",
    crushedBy: "Pure theory; being put on a stage",
    bound: "Does it for you instead of teaching you, then resents it",
  },
  {
    gifts: ["Teaching", "Mercy"],
    name: "The Gentle Guide",
    oneLine: "Helps hurting people understand what's happening to them.",
    craves: "One-on-one time; patience",
    crushedBy: "Rushed answers; cold advice",
    bound: "Explains pain away instead of sitting in it",
  },
  {
    gifts: ["Outreach", "Exhorting"],
    name: "The Rallier",
    oneLine: "Brings people in and fires them up.",
    craves: "New faces; momentum",
    crushedBy: "Closed doors; insider culture",
    bound: "Performs for the room and measures worth by response",
  },
  {
    gifts: ["Outreach", "Leading"],
    name: "The Mobilizer",
    oneLine: "Gathers people and organizes them into something that works.",
    craves: "Growth with structure; a clear mission",
    crushedBy: "Disorganized crowds; stagnation",
    bound: "Treats people as numbers to manage",
  },
  {
    gifts: ["Outreach", "Giving"],
    name: "The Generous Host",
    oneLine: "Opens their table and their wallet to bring people in.",
    craves: "Hospitality; new people at the table",
    crushedBy: "Stinginess; closed circles",
    bound: "Buys belonging and gives to be liked",
  },
  {
    gifts: ["Outreach", "Serving"],
    name: "The Servant Connector",
    oneLine: "Draws people in by serving them.",
    craves: "Practical ways to love outsiders; teamwork",
    crushedBy: "Talk without help; being made the face of it",
    bound: "Overextends to be wanted",
  },
  {
    gifts: ["Outreach", "Mercy"],
    name: "The Compassionate Inviter",
    oneLine: "Brings in the people others overlook.",
    craves: "Outsiders; time to listen",
    crushedBy: "Exclusive cultures; hurry",
    bound: "Collects hurting people in order to feel needed",
  },
  {
    gifts: ["Exhorting", "Leading"],
    name: "The Team Builder",
    oneLine: "Organizes people and believes in them until they rise.",
    craves: "A team with potential; clear goals",
    crushedBy: "Complacency; people who won't grow",
    bound: "Pushes and controls to look successful",
  },
  {
    gifts: ["Exhorting", "Giving"],
    name: "The Champion",
    oneLine: "Puts time and resources behind people they believe in.",
    craves: "People worth betting on; freedom to back them",
    crushedBy: "Ingratitude; people who coast",
    bound: "Gives to buy loyalty",
  },
  {
    gifts: ["Exhorting", "Serving"],
    name: "The Encouraging Helper",
    oneLine: "Helps you do it and believes you can.",
    craves: "Being in the trenches with people; practical needs",
    crushedBy: "Being sidelined; cynicism",
    bound: "Over-helps to be needed and cheers to avoid their own stuff",
  },
  {
    gifts: ["Exhorting", "Mercy"],
    name: "The Restorer",
    oneLine: "Sits with people in pain, then calls them back to life.",
    craves: "Time; people ready to heal",
    crushedBy: "Rushed fixes; shame",
    bound: "Rushes people out of pain to feel effective",
  },
  {
    gifts: ["Leading", "Giving"],
    name: "The Steward",
    oneLine: "Organizes resources so they go the furthest.",
    craves: "Clear budgets; trust; long horizons",
    crushedBy: "Waste; impulsive spending",
    bound: "Controls people through money",
  },
  {
    gifts: ["Leading", "Serving"],
    name: "The Operator",
    oneLine: "Runs the work and rolls up their sleeves to do it.",
    craves: "Clear tasks; reliable teams",
    crushedBy: "Chaos; credit-seekers",
    bound: "Overworks and can't delegate",
  },
  {
    gifts: ["Leading", "Mercy"],
    name: "The Guardian",
    oneLine: "Cares for people and gives them structure to grow.",
    craves: "Time with their people; a team that can stand on its own",
    crushedBy: "People treated as resources",
    bound: "Keeps people dependent so they're still needed",
  },
  {
    gifts: ["Giving", "Serving"],
    name: "The Quiet Provider",
    oneLine: "Meets needs with their hands and their resources.",
    craves: "Clear needs; being trusted to help",
    crushedBy: "Being taken for granted",
    bound: "The quiet martyr who gives everything and can't receive",
  },
  {
    gifts: ["Giving", "Mercy"],
    name: "The Comforter",
    oneLine: "Gives to people in pain, from the wallet and the heart.",
    craves: "Real needs; personal connection",
    crushedBy: "Cold institutions; manipulation",
    bound: "Gives until empty and rescues people who need to grow",
  },
  {
    gifts: ["Serving", "Mercy"],
    name: "The Caregiver",
    oneLine: "Shows up with practical help when life falls apart.",
    craves: "Being useful to hurting people; quiet roles",
    crushedBy: "Being ignored; boundaries ignored",
    bound: "Absorbs everyone's needs with no boundaries",
  },
];
